export const DEFAULT_ADMIN_LIST_OPTIONS = {
  page: 1,
  limit: 15,
}

export const ADMIN_LIST_SEARCH_MIN_LENGTH = 2

const sortEntries = value =>
  Object.entries(value || {}).sort(([keyA], [keyB]) => keyA.localeCompare(keyB))

const buildSortedObject = entries =>
  entries.reduce((acc, [key, value]) => {
    acc[key] = value
    return acc
  }, {})

export const normalizeAdminListOptions = (
  options,
  defaults = DEFAULT_ADMIN_LIST_OPTIONS
) => {
  const page = Number(options?.page)
  const limit = Number(options?.limit)
  const sortBy = options?.sortBy

  return {
    page: page > 0 ? page : defaults.page,
    limit: limit > 0 ? limit : defaults.limit,
    ...(sortBy ? { sortBy } : {}),
  }
}

export const resetAdminListPage = (
  options,
  defaults = DEFAULT_ADMIN_LIST_OPTIONS
) => ({
  ...normalizeAdminListOptions(options, defaults),
  page: 1,
})

export const normalizeAdminListFilters = (
  filters,
  { preserveEmptyKeys = [] } = {}
) =>
  buildSortedObject(
    sortEntries(filters).reduce((acc, [key, value]) => {
      if (value === undefined || value === null) return acc
      if (value === "" && !preserveEmptyKeys.includes(key)) return acc

      if (key === "search") {
        const search = String(value).trim()
        if (search) acc.push([key, search])
        return acc
      }

      acc.push([key, value])
      return acc
    }, [])
  )

export const removeAdminListSearchFilter = (
  filters,
  { preserveEmptyKeys = [] } = {}
) => {
  const { search, ...rest } = normalizeAdminListFilters(filters, {
    preserveEmptyKeys,
  })
  return rest
}

const getSearchText = searchText => String(searchText || "").trim()

export const shouldFetchAdminList = filters => {
  const search = getSearchText(filters?.search)
  return !search || search.length >= ADMIN_LIST_SEARCH_MIN_LENGTH
}

export const applyAdminListFilterChange = ({
  defaults = DEFAULT_ADMIN_LIST_OPTIONS,
  nextFilters,
  options,
  preserveEmptyKeys = [],
}) => ({
  filters: normalizeAdminListFilters(nextFilters, { preserveEmptyKeys }),
  options: resetAdminListPage(options, defaults),
})

export const applyAdminListSearchChange = ({
  defaults = DEFAULT_ADMIN_LIST_OPTIONS,
  filters,
  options,
  preserveEmptyKeys = [],
  searchText,
}) => {
  const inputText = String(searchText || "")
  const search = inputText.trim()

  if (search && search.length < ADMIN_LIST_SEARCH_MIN_LENGTH) {
    return { inputText, shouldUpdate: false }
  }

  if (!search) {
    return {
      filters: removeAdminListSearchFilter(filters, { preserveEmptyKeys }),
      inputText,
      options: resetAdminListPage(options, defaults),
      searchText: "",
      shouldUpdate: true,
    }
  }

  return {
    filters: {
      ...removeAdminListSearchFilter(filters, { preserveEmptyKeys }),
      search,
    },
    inputText,
    options: resetAdminListPage(options, defaults),
    searchText: search,
    shouldUpdate: true,
  }
}

export const createAdminListQueryKey = value => {
  if (!value || typeof value !== "object") return JSON.stringify(value)

  return JSON.stringify(
    sortEntries(value).reduce((acc, [key, item]) => {
      acc[key] =
        item && typeof item === "object" && !Array.isArray(item)
          ? JSON.parse(createAdminListQueryKey(item))
          : item
      return acc
    }, {})
  )
}
